import * as React from "react";
import { HelpCircle } from "lucide-react";

import { cn } from "@/lib/utils";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";

/**
 * Thin wrappers over the shadcn Tooltip so adding a hover explanation is a
 * one-liner. `TooltipProvider` is mounted once globally in App.tsx, so these
 * just need a Tooltip/Trigger/Content. Use `InfoTip` next to a label to render
 * a small help icon; use `HelpTip` to wrap an existing element (e.g. a button).
 */

export interface InfoTipProps {
  /** Text shown on hover. Keep it short and plain-language. */
  content: React.ReactNode;
  /** Which side the tooltip pops to. Defaults to "top". */
  side?: "top" | "right" | "bottom" | "left";
  /** Extra classes for the trigger icon. */
  className?: string;
  /** Accessible label for the icon button. Defaults to "More info". */
  label?: string;
}

/**
 * A small help icon that reveals `content` on hover/focus. Renders inline, so
 * drop it right after a label or computed figure:
 *   <span>Margin <InfoTip content={FORMULAS.margin} /></span>
 */
export function InfoTip({ content, side = "top", className, label = "More info" }: InfoTipProps) {
  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          onClick={(e) => e.preventDefault()}
          className={cn(
            "inline-flex items-center justify-center align-middle text-muted-foreground/70 hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-full",
            className,
          )}
        >
          <HelpCircle className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side={side} className="max-w-xs text-pretty leading-snug">
        {content}
      </TooltipContent>
    </Tooltip>
  );
}

export interface HelpTipProps {
  /** Text shown on hover. */
  content: React.ReactNode;
  /** The element the tooltip describes — typically a button. */
  children: React.ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  /** Delay before showing, in ms. Defaults to 150. Pass 0 for instant. */
  delayDuration?: number;
}

/**
 * Wraps an existing interactive element and adds a hover explanation without
 * each caller repeating the asChild/Content boilerplate:
 *   <HelpTip content="Permanently removes this line item — can't be undone">
 *     <Button>Delete</Button>
 *   </HelpTip>
 */
export function HelpTip({ content, children, side = "top", delayDuration = 150 }: HelpTipProps) {
  return (
    <Tooltip delayDuration={delayDuration}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side} className="max-w-xs text-pretty leading-snug">
        {content}
      </TooltipContent>
    </Tooltip>
  );
}
